import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ActiveSubscriptionBanner from './ActiveSubscriptionBanner';
import { ChartLine, Target, Wallet, User, Briefcase } from 'lucide-react';

/**
 * Dashboard component for authenticated users
 */
const DashboardAuth = () => {
  const { user, getToken, email } = useAuth();
  const navigate = useNavigate();
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  // Check subscription only once per user session
  const didCheckRef = React.useRef(false);
  useEffect(() => {
    if (!user) return;
    if (didCheckRef.current) return;
    didCheckRef.current = true;
    const checkSubscriptionStatus = async () => {
      if (!user || !email) {
        setCheckingSubscription(false);
        return;
      }
      try {
        setCheckingSubscription(true);
        const token = await getToken();
        // GET with explicit email query param
        const url = `/api/check-subscription?email=${encodeURIComponent(email)}`;
        console.log('[DashboardAuth] Fetching subscription with GET query:', url);
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) {
          console.error('Failed to check subscription status:', await response.text());
          return;
        }
        const data = await response.json();
        setHasActiveSubscription(data.hasActiveSubscription);
      } catch (err) {
        console.error('Error checking subscription status:', err);
      } finally {
        setCheckingSubscription(false);
      }
    };
    checkSubscriptionStatus();
  }, [user, getToken, email]); // Added getToken and email to dependency array

  // Show loading state while checking subscription
  if (checkingSubscription) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-8">
        <div className="h-12 w-48 bg-gray-200 rounded animate-pulse mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4].map(i => ( // Adjusted for 4 cards
            <div key={i} className="h-64 bg-gray-100 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-8">
      {hasActiveSubscription && <ActiveSubscriptionBanner />}
      
      <h1 className="text-3xl font-bold mb-8 flex items-center justify-center"> {/* Increased mb and icon size */}
        <User className="mr-3 h-8 w-8 text-blue-500" /> {/* Increased icon size and margin */}
        Welcome, {user?.fullName?.split(' ')[0]}
      </h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="transition-transform duration-200 hover:scale-105 hover:shadow-xl flex flex-col"> {/* Added flex flex-col to Card */}
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center">
              <ChartLine className="mr-2 h-6 w-6 text-blue-500" /> {/* Increased icon size */}
              Options Strategies
            </CardTitle>
            <CardDescription>Analyze and build trading strategies</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col flex-grow"> {/* Added flex flex-col and flex-grow */}
            <p className="text-sm mb-6"> {/* Increased mb */}
              Explore option strategies with our robust analysis tools.
            </p>
            <Button onClick={() => navigate('/optionstrategy')} className="w-full mt-auto"> {/* Added mt-auto */}
              Start Analysis
            </Button>
          </CardContent>
        </Card>

        <Card className="transition-transform duration-200 hover:scale-105 hover:shadow-xl flex flex-col"> {/* Added flex flex-col to Card */}
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center">
              <Briefcase className="mr-2 h-6 w-6 text-yellow-500" /> {/* Increased icon size */}
              Portfolio
            </CardTitle>
            <CardDescription>View your connected accounts and portfolio</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col flex-grow"> {/* Added flex flex-col and flex-grow */}
            <p className="text-sm mb-6"> {/* Increased mb */}
              Manage your brokerage connections and view your portfolio positions.
            </p>
            <Button onClick={() => navigate('/portfolio')} className="w-full mt-auto"> {/* Added mt-auto */}
              View Portfolio
            </Button>
          </CardContent>
        </Card>
        
        <Card className="transition-transform duration-200 hover:scale-105 hover:shadow-xl flex flex-col"> {/* Added flex flex-col to Card */}
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center">
              <Target className="mr-2 h-6 w-6 text-green-500" /> {/* Increased icon size */}
              Market Trends
            </CardTitle>
            <CardDescription>Explore current market conditions</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col flex-grow"> {/* Added flex flex-col and flex-grow */}
            <p className="text-sm mb-6"> {/* Increased mb */}
              Market trend analysis features are coming soon. Stay tuned for updates.
            </p>
            <Button 
              className="w-full mt-auto" /* Added mt-auto */
              variant="outline"
              disabled
            >
              Coming Soon
            </Button>
          </CardContent>
        </Card>
        
        <Card className="transition-transform duration-200 hover:scale-105 hover:shadow-xl flex flex-col"> {/* Added flex flex-col to Card */}
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center">
              <Wallet className="mr-2 h-6 w-6 text-purple-500" /> {/* Increased icon size */}
              Account
            </CardTitle>
            <CardDescription>Manage your subscription</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col flex-grow"> {/* Added flex flex-col and flex-grow */}
            <p className="text-sm mb-6"> {/* Increased mb */}
              {hasActiveSubscription 
                ? "View and manage your active subscription."
                : "No active subscription. Subscribe now to unlock features."}
            </p>
            <Button 
              onClick={() => navigate('/subscription')} 
              className="w-full mt-auto" /* Added mt-auto */
              variant={hasActiveSubscription ? "default" : "outline"}
            >
              {hasActiveSubscription ? "Manage Account" : "My Subscription"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardAuth;
